import { MODULES, PERMISSIONS } from '@ibe/constants';
import { StatusBadge, toneForStatus } from '@ibe/ui';
import { useParams, Link } from 'react-router-dom';

import { useAuth } from '../../../auth/auth-context.js';
import { ResourceDetailPage } from '../../../components/resource-detail/resource-detail-page.js';
import { DetailField, DetailGrid } from '../../../components/resource-detail/detail-grid.js';
import { useReservation } from '../api/queries.js';
import { type ReservationDetail, type ReservationItem } from '../api/schemas.js';

/**
 * One reservation, with everything an operator needs to honour or change it.
 *
 * Property and guest names are links that open in a new tab, because an
 * operator verifying a booking often needs the property and guest records
 * without losing the reservation they were reading.
 */

function formatDate(value: string | undefined): string {
  if (value === undefined) return '';
  return new Date(value).toLocaleDateString('en-AU', {
    timeZone: 'UTC',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatDateTime(value: string | undefined): string {
  if (value === undefined) return '';
  return new Date(value).toLocaleString('en-AU', {
    timeZone: 'UTC',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function PropertyLink({
  id,
  name,
}: {
  readonly id: string;
  readonly name: string;
}): React.JSX.Element {
  return (
    <Link
      to={`/properties/${id}`}
      target="_blank"
      rel="noopener noreferrer"
      className="font-medium underline-offset-4 hover:underline"
    >
      {name}
    </Link>
  );
}

function GuestLink({
  id,
  name,
}: {
  readonly id: string;
  readonly name: string;
}): React.JSX.Element {
  return (
    <Link
      to={`/guests/${id}`}
      target="_blank"
      rel="noopener noreferrer"
      className="font-medium underline-offset-4 hover:underline"
    >
      {name}
    </Link>
  );
}

function ReservationItemsTable({
  items,
}: {
  readonly items: readonly ReservationItem[];
}): React.JSX.Element {
  return (
    <table className="w-full text-sm">
      <thead className="text-left text-muted-foreground">
        <tr>
          <th className="pb-2 font-medium">Room type</th>
          <th className="pb-2 font-medium">Rate plan</th>
          <th className="pb-2 font-medium">Check-in</th>
          <th className="pb-2 font-medium">Check-out</th>
          <th className="pb-2 font-medium text-right">Nights</th>
          <th className="pb-2 font-medium text-right">Adults</th>
          <th className="pb-2 font-medium text-right">Children</th>
          <th className="pb-2 font-medium text-right">Infants</th>
          <th className="pb-2 font-medium text-right">Total</th>
        </tr>
      </thead>
      <tbody className="divide-y">
        {items.map((item) => (
          <tr key={item.id}>
            <td className="py-2 pr-4 align-top">{item.roomTypeName}</td>
            <td className="py-2 pr-4 align-top">{item.ratePlanName}</td>
            <td className="py-2 pr-4 align-top">{formatDate(item.checkIn)}</td>
            <td className="py-2 pr-4 align-top">{formatDate(item.checkOut)}</td>
            <td className="py-2 text-right">{String(item.nights)}</td>
            <td className="py-2 text-right">{String(item.adults)}</td>
            <td className="py-2 text-right">{String(item.children)}</td>
            <td className="py-2 text-right">{String(item.infants)}</td>
            <td className="py-2 text-right">{item.totalFormatted}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ReservationDetailContent({
  data,
}: {
  readonly data: ReservationDetail;
}): React.JSX.Element {
  const auth = useAuth();

  return (
    <>
      <section className="space-y-2">
        <h2 className="text-body font-semibold">Booking details</h2>
        <DetailGrid>
          <DetailField label="Reference">{data.reference}</DetailField>
          <DetailField label="Status">
            <StatusBadge tone={toneForStatus(data.status)}>{data.status}</StatusBadge>
          </DetailField>
          <DetailField label="Source">{data.source}</DetailField>
          {data.source === 'PMS' && auth.hasModule(MODULES.PMS) && (
            <DetailField label="PMS reservation ID">{data.pmsReservationId ?? '—'}</DetailField>
          )}
          {data.source === 'OTA' && (
            <DetailField label="OTA name">{data.otaName ?? '—'}</DetailField>
          )}
          <DetailField label="Property">
            <PropertyLink id={data.propertyId} name={data.propertyName} />
          </DetailField>
          <DetailField label="Guest">
            <GuestLink id={data.guestId} name={data.guestName} />
          </DetailField>
          <DetailField label="Currency">{data.currency}</DetailField>
          <DetailField label="Check-in">{formatDate(data.checkIn)}</DetailField>
          <DetailField label="Check-out">{formatDate(data.checkOut)}</DetailField>
          <DetailField label="Nights">{String(data.nights)}</DetailField>
          <DetailField label="Rooms">{String(data.rooms)}</DetailField>
          <DetailField label="Adults">{String(data.adults)}</DetailField>
          <DetailField label="Children">{String(data.children)}</DetailField>
          <DetailField label="Infants">{String(data.infants)}</DetailField>
          <DetailField label="Reserved">{formatDateTime(data.createdAt)}</DetailField>
          <DetailField label="Total">{data.totalFormatted}</DetailField>
          <DetailField label="Special requests">
            {data.specialRequests === null || data.specialRequests === ''
              ? '—'
              : data.specialRequests}
          </DetailField>
        </DetailGrid>
      </section>

      <section className="space-y-2 pt-4">
        <h2 className="text-body font-semibold">Items</h2>
        {data.items.length === 0 ? (
          <p className="text-sm text-muted-foreground">No items on this reservation.</p>
        ) : (
          <ReservationItemsTable items={data.items} />
        )}
      </section>
    </>
  );
}

export function ReservationDetailPage(): React.JSX.Element {
  const reservationId = useParams().reservationId ?? '';
  const query = useReservation(reservationId);

  return (
    <ResourceDetailPage
      query={query}
      permission={PERMISSIONS.RESERVATION_READ}
      parent={{ to: '/reservations', label: 'Reservations' }}
      title={(data) => data.reference}
    >
      {(data) => <ReservationDetailContent data={data} />}
    </ResourceDetailPage>
  );
}
